import React from 'react';
import axios, {type CancelTokenSource} from 'axios';
import {stringifyUrl} from 'query-string';
import EditRounded from '@mui/icons-material/EditRounded';
import Visibility from '@mui/icons-material/Visibility';
import Api from '../../../core/services/api/api';
import CacheService from '../../../core/services/cache/cache_service';
import Regexes from '../../../core/models/regexes';
import Utils from '../../../core/services/utils/utils';
import {NexusSearchSchemaEntryNames} from '../../../core/constants/enums';
import {routeFunctions, routes} from '../../../core/models/static/routes/app-routes';
import {
    type CommandSearchContext,
    type CommandSearchProvider,
    type CommandSearchResult,
    type CommandSearchRowAction,
    getScopeFrameId,
    scoreCommandSearchFields,
} from '../../components/panel-command-search';
import type {PanelAccessServices} from '../types';
import {buildNexusIndustryCategoryId} from '../categories';

export interface DynamicSearchIndustry {
    id: string;
    title?: string;
    name?: string;
    iconFileName?: string;
}

export interface OpenCreateOnlineProfileArgs {
    initialValues: { company: { id?: number | string; code?: string; name?: string } };
    onSuccess: () => void | Promise<void>;
    onClose: () => void;
}

export interface DynamicSearchServices extends PanelAccessServices {
    industries?: DynamicSearchIndustry[];
    defaultIndustryId?: string | number;
    canSeeAutocompleteSuggestions?: boolean;
    /** i18n under `panel-sections.command-search` (selector style). */
    tCommandSearch?: (selector: (t: any) => string, options?: Record<string, unknown>) => string;
    /**
     * Opens the create-online-profile dialog (mirrors sales list views).
     * Callers should keep the command palette open until this resolves.
     */
    openCreateOnlineProfile?: (args: OpenCreateOnlineProfileArgs) => void;
}

const getServices = (ctx: CommandSearchContext): DynamicSearchServices | undefined =>
    ctx.services as DynamicSearchServices | undefined;

const stripHtml = (value: string): string => value.replace(/<[^>]+>/g, '');

const normalizeVinQuery = (value: string): string => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const buildVinSummary = (data?: Record<string, unknown>): string => {
    const firstLine = [data?.make, data?.model].filter(Boolean).join(', ');
    const year = data?.year ? ` (${data.year})` : '';
    const secondLine = [data?.tier, data?.bodyClass].filter(Boolean).join(', ');
    return [firstLine ? `${firstLine}${year}` : '', secondLine].filter(Boolean).join(' · ');
};

/**
 * When the partner disallows special characters in Nexus search, strip them so
 * suggestions still run on the sanitized keyword (same rules as search-autocomplete).
 */
const sanitizeNexusSearchKeyword = (keyword: string, partnerId?: string | null): string => {
    const trimmed = keyword?.trim() ?? '';
    if (!trimmed || Utils.skipSpecialCharacterError(partnerId ?? undefined)) {
        return trimmed;
    }
    return trimmed.replace(new RegExp(Regexes.specialCharacters.source, Regexes.specialCharacters.flags), '');
};

/** Bridges provider-level `AbortController`s to an axios `CancelToken` source. */
export const abortToCancelToken = (abortController: AbortController): CancelTokenSource => {
    const source = axios.CancelToken.source();
    abortController.signal.addEventListener('abort', () => source.cancel());
    return source;
};

const NEXUS_RESULTS_PER_INDUSTRY = 5;
const PART_LOOKUP_RESULTS_PER_INDUSTRY = 8;
const MIN_PART_LOOKUP_QUERY_LENGTH = 3;
const MIN_VIN_QUERY_LENGTH = 11;

/** Display labels for Nexus suggestion schema entry names (mirrors legacy autocomplete titles). */
const NEXUS_SUGGESTION_TYPE_TITLES: Record<string, string> = {
    [NexusSearchSchemaEntryNames.partNo]: 'Part No',
    [NexusSearchSchemaEntryNames.tag]: 'Tag',
    [NexusSearchSchemaEntryNames.code]: 'Code',
    [NexusSearchSchemaEntryNames.category]: 'Category',
    [NexusSearchSchemaEntryNames.parentCategory]: 'Parent Category',
    [NexusSearchSchemaEntryNames.variety]: 'Variety',
};

interface PartLookupHit {
    id?: string;
    partNo: string;
}

const resolveNexusSuggestionTypeTitle = (name?: string): string => {
    if (!name) {
        return 'Keyword';
    }
    return NEXUS_SUGGESTION_TYPE_TITLES[name] ?? 'Keyword';
};

/** True when the operator can edit and/or view catalogue product information. */
const hasPartLookupAccess = (services?: DynamicSearchServices): boolean =>
    Boolean(
        services?.canAccess?.('lookup.productInformation')
        || services?.canAccess?.('lookup.lookupProductInformation'),
    );

const normalizePartNoKey = (partNo: string): string => partNo.trim().toUpperCase();

/**
 * Builds ACL-gated Edit / View trailing actions for a part-number result row.
 */
const buildPartRowActions = (
    ctx: CommandSearchContext,
    services: DynamicSearchServices | undefined,
    part: PartLookupHit,
): CommandSearchRowAction[] => {
    const actions: CommandSearchRowAction[] = [];

    if (services?.canAccess?.('lookup.productInformation') && part.id) {
        const partId = part.id;
        const editTo = routeFunctions.products.single(partId, undefined);
        actions.push({
            id: 'edit',
            label: services?.tCommandSearch?.((t) => t.editPart) ?? 'Edit part',
            icon: <EditRounded fontSize="small"/>,
            to: editTo,
            onClick: () => ctx.navigate(editTo),
        });
    }

    if (services?.canAccess?.('lookup.lookupProductInformation') && part.partNo) {
        const viewTo = routeFunctions.lookup.single(part.partNo);
        actions.push({
            id: 'view',
            label: services?.tCommandSearch?.((t) => t.viewPart) ?? 'View part',
            icon: <Visibility fontSize="small"/>,
            to: viewTo,
            onClick: () => ctx.navigate(viewTo),
        });
    }

    return actions;
};

/**
 * Maps a catalogue part hit to a command-search result (primary select = View).
 */
const buildPartCommandSearchResult = (
    ctx: CommandSearchContext,
    services: DynamicSearchServices | undefined,
    args: {
        industry: DynamicSearchIndustry;
        keyword: string;
        part: PartLookupHit;
        categoryId: string;
    },
): CommandSearchResult => {
    const {industry, keyword, part, categoryId} = args;
    const industryLabel = industry.title ?? industry.name;
    const typeTitle = resolveNexusSuggestionTypeTitle(NexusSearchSchemaEntryNames.partNo);
    const partNo = part.partNo;
    const viewTo = routeFunctions.lookup.single(partNo);
    const rowActions = buildPartRowActions(ctx, services, part);

    return {
        id: `nexus-${industry.id}-partNo-${part.id ?? partNo}`,
        category: categoryId,
        title: `${typeTitle} · ${partNo}`,
        subtitle: industryLabel,
        badge: typeTitle,
        badgeColor: 'info',
        score: scoreCommandSearchFields(keyword, {
            title: partNo,
            subtitle: industryLabel,
            identifiers: [partNo],
            type: typeTitle,
            category: categoryId,
            keywords: [partNo],
        }),
        providerId: 'nexus-search',
        to: viewTo,
        recentMeta: {
            actionKey: 'navigate',
            actionPayload: {to: viewTo},
        },
        ...(rowActions.length ? {rowActions} : {}),
        onSelect: () => ctx.navigate(viewTo),
    };
};

const buildNexusSearchUrl = (keyword: string, industryId?: string | number): string =>
    stringifyUrl({
        url: routes.lookup.search,
        query: {
            keyword: keyword.trim(),
            industryId: industryId ?? undefined,
        },
    });

const buildVinLookupSearchUrl = (vin: string, industryId?: string | number): string =>
    stringifyUrl({
        url: routes.lookup.search,
        query: {
            vin,
            industryId: industryId ?? undefined,
        },
    });

const navigateToNexusSearch = (
    ctx: CommandSearchContext,
    keyword: string,
    industryId?: string | number,
): void => {
    ctx.navigate(buildNexusSearchUrl(keyword, industryId));
};

const navigateToVinLookupSearch = (
    ctx: CommandSearchContext,
    vin: string,
    industryId?: string | number,
): void => {
    ctx.navigate(buildVinLookupSearchUrl(vin, industryId));
};

const vinDecoderProvider: CommandSearchProvider = {
    id: 'vin-decoder-search',
    category: 'vinDecoder',
    search: async (ctx) => {
        const services = getServices(ctx);
        const vin = normalizeVinQuery(ctx.query);
        if (vin.length < MIN_VIN_QUERY_LENGTH) {
            return [];
        }

        const enabledIndustries = (services?.industries ?? []).filter(Utils.isVinDecoderIndustry);
        if (!enabledIndustries.length) {
            return [];
        }

        const cancelSource = abortToCancelToken(ctx.abortController);
        const response = await Api.decodeVin(vin, cancelSource.token as any);
        if (!response?.resultFlag || !response.data || Object.keys(response.data).length === 0) {
            return [];
        }

        const summary = buildVinSummary(response.data);
        const badge = services?.tCommandSearch?.((t) => t.categories.vinDecoder) ?? 'VIN Decoder';

        return enabledIndustries.map((industry) => {
            const industryLabel = industry.title ?? industry.name ?? String(industry.id);
            const title = services?.tCommandSearch?.(
                (t) => t.vinResultTitle,
                {industry: industryLabel},
            ) ?? `VIN Decoder · ${industryLabel}`;
            const subtitle = summary
                ? (services?.tCommandSearch?.(
                    (t) => t.vinResultSubtitle,
                    {vin, summary},
                ) ?? [vin, summary].filter(Boolean).join(' · '))
                : vin;

            return {
                id: `vin-decoder-${industry.id}-${vin}`,
                category: 'vinDecoder',
                title,
                subtitle,
                badge,
                badgeColor: 'info' as const,
                score: scoreCommandSearchFields(vin, {
                    title: `${industryLabel} ${summary}`.trim(),
                    subtitle,
                    identifiers: [vin],
                    keywords: [industryLabel, summary],
                    type: 'VIN Decoder',
                    category: 'vinDecoder',
                }),
                providerId: 'vin-decoder-search',
                to: buildVinLookupSearchUrl(vin, industry.id),
                recentMeta: {
                    actionKey: 'navigate',
                    actionPayload: {
                        to: buildVinLookupSearchUrl(vin, industry.id),
                    },
                },
                onSelect: () => navigateToVinLookupSearch(ctx, vin, industry.id),
            };
        });
    },
    enabled: (ctx) => {
        const services = getServices(ctx);
        return Boolean(services?.canAccess?.('lookup.vinDecoder'))
            && Boolean((services?.industries ?? []).some(Utils.isVinDecoderIndustry))
            && normalizeVinQuery(ctx.query).length >= MIN_VIN_QUERY_LENGTH;
    },
};

/** Cross-industry Nexus suggestions + catalogue part lookup (merged / deduped per industry). */
const nexusSearchProvider: CommandSearchProvider = {
    id: 'nexus-search',
    category: 'nexus',
    search: async (ctx) => {
        const services = getServices(ctx);
        const displayKeyword = ctx.query.trim();
        const keyword = sanitizeNexusSearchKeyword(displayKeyword, CacheService.getOperatorPartnerId());
        const hasNexusAccess = Boolean(services?.canAccess?.('lookup.nexusSearch'));
        const canFetchParts = hasPartLookupAccess(services) && keyword.length >= MIN_PART_LOOKUP_QUERY_LENGTH;
        const canFetchSuggestions = hasNexusAccess
            && Boolean(services?.canSeeAutocompleteSuggestions)
            && keyword.length >= 2;
        const canShowKeywordRows = hasNexusAccess
            && !services?.canSeeAutocompleteSuggestions
            && keyword.length >= 2;

        if (!keyword) {
            return [];
        }
        if (!canFetchSuggestions && !canFetchParts && !canShowKeywordRows) {
            return [];
        }

        const industries = services?.industries ?? [];
        const results: CommandSearchResult[] = [];

        // Keyword navigation rows when suggestions are disabled (Nexus ACL only).
        if (canShowKeywordRows) {
            if (!industries.length) {
                const title = services?.tCommandSearch?.(
                    (t) => t.nexusKeywordSearch,
                    {query: displayKeyword},
                ) ?? `Search Nexus for “${displayKeyword}”`;
                const subtitle = services?.tCommandSearch?.(
                    (t) => t.nexusKeywordSearchSubtitle,
                ) ?? 'Open Nexus search results';
                const badge = services?.tCommandSearch?.((t) => t.categories.nexus) ?? 'Nexus';
                const keywordTo = buildNexusSearchUrl(keyword, services?.defaultIndustryId);

                results.push({
                    id: `nexus-keyword-${keyword}`,
                    category: 'nexus',
                    title,
                    subtitle,
                    badge,
                    badgeColor: 'info',
                    score: 1,
                    providerId: 'nexus-search',
                    to: keywordTo,
                    recentMeta: {
                        actionKey: 'navigate',
                        actionPayload: {to: keywordTo},
                    },
                    onSelect: () => navigateToNexusSearch(ctx, keyword, services?.defaultIndustryId),
                });
            } else {
                const nexusLabel = services?.tCommandSearch?.((t) => t.categories.nexus) ?? 'Nexus search';

                industries.forEach((industry) => {
                    const industryLabel = industry.title ?? industry.name ?? String(industry.id);
                    const title = services?.tCommandSearch?.(
                        (t) => t.nexusIndustryKeywordSearch,
                        {query: displayKeyword, industry: industryLabel},
                    ) ?? `Search ${industryLabel} for “${displayKeyword}”`;
                    const keywordTo = buildNexusSearchUrl(keyword, industry.id);

                    results.push({
                        id: `nexus-keyword-${industry.id}-${keyword}`,
                        category: 'nexus',
                        title,
                        subtitle: industryLabel,
                        badge: nexusLabel,
                        badgeColor: 'info',
                        score: 1,
                        providerId: 'nexus-search',
                        to: keywordTo,
                        recentMeta: {
                            actionKey: 'navigate',
                            actionPayload: {to: keywordTo},
                        },
                        onSelect: () => navigateToNexusSearch(ctx, keyword, industry.id),
                    });
                });
            }
        }

        if (!industries.length || (!canFetchSuggestions && !canFetchParts)) {
            return results;
        }

        const cancelSource = abortToCancelToken(ctx.abortController);
        /** Part-number rows that still need catalogue ids for Edit actions. */
        const pendingPartIdByResultId = new Map<string, {
            industry: DynamicSearchIndustry;
            partNo: string;
            categoryId: string;
            keyword: string;
        }>();

        const perIndustry = await Promise.allSettled(
            industries.map(async (industry) => {
                const [suggestionResponse, partResponse] = await Promise.all([
                    canFetchSuggestions
                        ? Api.getNexusSearchSuggestion(
                            {
                                industryId: industry.id,
                                isAdvancedMode: true,
                                keyword,
                                nexusSuggestionTag: {highlightPreTag: '<strong>', highlightPostTag: '</strong>'},
                            },
                            cancelSource.token,
                        )
                        : Promise.resolve(null),
                    canFetchParts
                        ? Api.autoCompletePartLookup(keyword, industry.id, cancelSource.token)
                        : Promise.resolve(null),
                ]);

                const suggestionItems = suggestionResponse?.resultFlag
                    ? (suggestionResponse.data ?? [])
                    : [];
                const partItems: PartLookupHit[] = partResponse?.resultFlag
                    ? ((partResponse.data ?? []) as Array<{ id?: string; partNo?: string }>)
                        .map((item) => ({
                            id: item.id != null ? String(item.id) : undefined,
                            partNo: item.partNo ?? '',
                        }))
                        .filter((item) => Boolean(item.partNo))
                        .slice(0, PART_LOOKUP_RESULTS_PER_INDUSTRY)
                    : [];

                return {industry, suggestionItems, partItems};
            }),
        );

        perIndustry.forEach((outcome) => {
            if (outcome.status !== 'fulfilled') {
                return;
            }

            const {industry, suggestionItems, partItems} = outcome.value;
            const industryLabel = industry.title ?? industry.name;
            const categoryId = buildNexusIndustryCategoryId(industry.id);

            const partsByKey = new Map<string, PartLookupHit>();
            partItems.forEach((part) => {
                const key = normalizePartNoKey(part.partNo);
                if (key && !partsByKey.has(key)) {
                    partsByKey.set(key, part);
                }
            });

            const items = (suggestionItems ?? []).slice(0, NEXUS_RESULTS_PER_INDUSTRY);

            items.forEach((item: { value?: string; displayValue?: string; name?: string }, index: number) => {
                const value = item.value ?? '';
                const display = stripHtml(item.displayValue ?? item.value ?? '');
                if (!display) {
                    return;
                }

                const typeTitle = resolveNexusSuggestionTypeTitle(item.name);
                const title = `${typeTitle} · ${display}`;
                const isPartNumber = item.name === NexusSearchSchemaEntryNames.partNo;
                const matchedPart = isPartNumber ? partsByKey.get(normalizePartNoKey(value)) : undefined;

                if (isPartNumber) {
                    if (matchedPart) {
                        partsByKey.delete(normalizePartNoKey(value));
                    }
                    const part: PartLookupHit = {
                        id: matchedPart?.id,
                        partNo: value || matchedPart?.partNo || display,
                    };
                    const partResult = buildPartCommandSearchResult(ctx, services, {
                        industry,
                        keyword,
                        part,
                        categoryId,
                    });
                    results.push(partResult);
                    if (!part.id && part.partNo) {
                        pendingPartIdByResultId.set(partResult.id, {
                            industry,
                            partNo: part.partNo,
                            categoryId,
                            keyword,
                        });
                    }
                    return;
                }

                const resultTo = buildNexusSearchUrl(value, industry.id);

                results.push({
                    id: `nexus-${industry.id}-${item.name ?? index}-${value}`,
                    category: categoryId,
                    title,
                    subtitle: industryLabel,
                    badge: typeTitle,
                    badgeColor: 'info',
                    score: scoreCommandSearchFields(keyword, {
                        title: display,
                        subtitle: industryLabel,
                        identifiers: [value],
                        type: typeTitle,
                        category: categoryId,
                        keywords: [value, typeTitle],
                    }),
                    providerId: 'nexus-search',
                    to: resultTo,
                    recentMeta: {
                        actionKey: 'navigate',
                        actionPayload: {to: resultTo},
                    },
                    onSelect: () => navigateToNexusSearch(ctx, value, industry.id),
                });
            });

            // Remaining catalogue parts not already covered by a Nexus partNo suggestion.
            partsByKey.forEach((part) => {
                const partResult = buildPartCommandSearchResult(ctx, services, {
                    industry,
                    keyword,
                    part,
                    categoryId,
                });
                results.push(partResult);
                if (!part.id && part.partNo) {
                    pendingPartIdByResultId.set(partResult.id, {
                        industry,
                        partNo: part.partNo,
                        categoryId,
                        keyword,
                    });
                }
            });
        });

        if (
            pendingPartIdByResultId.size
            && hasPartLookupAccess(services)
        ) {
            const partNumbers = [...new Set(
                [...pendingPartIdByResultId.values()].map((entry) => entry.partNo),
            )];
            const partInfoResponse = await Api.getPartInfoWithPartNumber(
                {partNumbers},
                false,
                cancelSource.token,
            );

            if (partInfoResponse?.resultFlag && Array.isArray(partInfoResponse.data)) {
                const idByPartNo = new Map<string, string>();
                (partInfoResponse.data as Array<{ partNo?: string; id?: string | number }>).forEach((row) => {
                    if (!row.partNo || row.id == null) {
                        return;
                    }
                    idByPartNo.set(normalizePartNoKey(row.partNo), String(row.id));
                });

                pendingPartIdByResultId.forEach((pending, resultId) => {
                    const resolvedId = idByPartNo.get(normalizePartNoKey(pending.partNo));
                    if (!resolvedId) {
                        return;
                    }
                    const index = results.findIndex((result) => result.id === resultId);
                    if (index < 0) {
                        return;
                    }
                    results[index] = buildPartCommandSearchResult(ctx, services, {
                        industry: pending.industry,
                        keyword: pending.keyword,
                        part: {id: resolvedId, partNo: pending.partNo},
                        categoryId: pending.categoryId,
                    });
                });
            }
        }

        return results;
    },
    enabled: (ctx) => {
        const services = getServices(ctx);
        const keyword = sanitizeNexusSearchKeyword(ctx.query, CacheService.getOperatorPartnerId());
        const hasNexusAccess = Boolean(services?.canAccess?.('lookup.nexusSearch'));
        const hasPartAccess = hasPartLookupAccess(services);

        return (hasNexusAccess && keyword.length >= 2)
            || (hasPartAccess && keyword.length >= MIN_PART_LOOKUP_QUERY_LENGTH);
    },
};

const CUSTOMERS_RESULT_LIMIT = 8;

/** Customer directory search (hidden while already inside a customer scope). */
const customersSearchProvider: CommandSearchProvider = {
    id: 'customers-search',
    category: 'customers',
    search: async (ctx) => {
        const cancelSource = abortToCancelToken(ctx.abortController);
        const response = await Api.autoCompleteCustomersV2(ctx.query, cancelSource.token as any);
        if (!response?.resultFlag) {
            return [];
        }

        const items = (response.data ?? []) as Array<{ id: string; name: string; code?: string }>;

        return items.slice(0, CUSTOMERS_RESULT_LIMIT).map((customer) => {
            const to = routeFunctions.customer.generalInformation.profile(Number(customer.id));
            return {
                id: `customer-${customer.id}`,
                category: 'customers',
                title: customer.name,
                subtitle: customer.code ? `#${customer.code}` : undefined,
                badge: 'Customer',
                badgeColor: 'primary' as const,
                score: scoreCommandSearchFields(ctx.query, {
                    title: customer.name,
                    subtitle: customer.code ? `#${customer.code}` : undefined,
                    identifiers: customer.code ? [customer.code] : undefined,
                    type: 'Customer',
                    category: 'customers',
                }),
                providerId: 'customers-search',
                to,
                recentMeta: {
                    actionKey: 'navigate',
                    actionPayload: {to},
                },
                onSelect: () => ctx.navigate(to),
            };
        });
    },
    enabled: (ctx) => {
        const services = getServices(ctx);
        return Boolean(services?.canAccess?.('base.customerList'))
            && !getScopeFrameId(ctx.scope, 'customer')
            && ctx.query.trim().length > 0;
    },
};

interface ScopedOrderCustomer {
    id?: number | string;
    customerNo?: string;
    name?: string;
}

interface ScopedOrderResult {
    id: number;
    orderId?: number;
    orderNo?: string;
    invoiceNo?: string;
    customer?: ScopedOrderCustomer;
    customerPO?: string;
}

/**
 * Resolves the online customer profile id via customerNo (never ERP company id),
 * prompting create-online-profile when needed — same flow as sales list views.
 */
const resolveOnlineCustomerIdForOrder = async (
    ctx: CommandSearchContext,
    customer: ScopedOrderCustomer | undefined,
): Promise<number | undefined> => {
    if (!customer?.customerNo) {
        return undefined;
    }

    const response = await Api.getCustomerSimpleInfoByCode(customer.customerNo);
    if (response?.resultFlag && response.data?.id) {
        return Number(response.data.id);
    }

    const openCreateOnlineProfile = getServices(ctx)?.openCreateOnlineProfile;
    if (!openCreateOnlineProfile) {
        return undefined;
    }

    return await new Promise<number | undefined>((resolve) => {
        let succeeded = false;
        openCreateOnlineProfile({
            initialValues: {
                company: {
                    id: customer.id,
                    code: customer.customerNo,
                    name: customer.name,
                },
            },
            onClose: () => {
                if (!succeeded) {
                    resolve(undefined);
                }
            },
            onSuccess: async () => {
                succeeded = true;
                const retry = await Api.getCustomerSimpleInfoByCode(customer.customerNo!);
                resolve(retry?.data?.id ? Number(retry.data.id) : undefined);
            },
        });
    });
};

/** Resolves online customer id then navigates. Returns false to keep the palette open. */
const navigateToScopedOrder = async (
    ctx: CommandSearchContext,
    order: ScopedOrderResult,
    buildRoute: (customerId: number, orderId: number) => string,
): Promise<boolean | void> => {
    const orderId = order.orderId ?? order.id;
    if (!orderId) {
        return false;
    }

    const customerId = await resolveOnlineCustomerIdForOrder(ctx, order.customer);
    if (customerId === undefined) {
        // Cancelled profile dialog or failed lookup — keep palette open.
        return false;
    }

    ctx.navigate(buildRoute(customerId, orderId));
};

const ORDER_SEARCH_RESULT_LIMIT = 8;

interface CreateOrderSearchProviderConfig {
    id: string;
    category: string;
    accessKey: string;
    badge: string;
    /** Whether the API's customer filter key is capitalized (`CustomerId`) or not (`customerId`). */
    uppercaseCustomerFilterKey?: boolean;
    /** Resolves the result title. Defaults to `orderNo` (invoices/credits use `invoiceNo`). */
    getTitle?: (order: ScopedOrderResult) => string | undefined;
    fetch: (payload: Record<string, unknown>, cancelToken: CancelTokenSource['token']) => Promise<{
        resultFlag?: boolean;
        data?: { resultList?: ScopedOrderResult[] };
    }>;
    buildRoute: (customerId: number, orderId: number) => string;
}

const createOrderSearchProvider = (config: CreateOrderSearchProviderConfig): CommandSearchProvider => ({
    id: config.id,
    category: config.category,
    search: async (ctx) => {
        const scopedCustomerId = getScopeFrameId(ctx.scope, 'customer');
        const cancelSource = abortToCancelToken(ctx.abortController);
        const customerFilterKey = config.uppercaseCustomerFilterKey ? 'CustomerId' : 'customerId';

        const payload = {
            filters: {
                Keyword: ctx.query,
                [customerFilterKey]: scopedCustomerId ? [Number(scopedCustomerId)] : undefined,
            },
            paginationInfo: {pageSize: ORDER_SEARCH_RESULT_LIMIT, currentPage: 1},
        };

        const response = await config.fetch(payload, cancelSource.token);
        if (!response?.resultFlag) {
            return [];
        }

        const rows = response.data?.resultList ?? [];

        return rows.slice(0, ORDER_SEARCH_RESULT_LIMIT).map((order) => {
            const orderId = order.orderId ?? order.id;
            const title = config.getTitle?.(order) ?? order.orderNo ?? String(orderId);
            const identifiers = [
                order.invoiceNo,
                order.orderNo,
                String(orderId),
            ].filter(Boolean) as string[];

            return {
                id: `${config.id}-${orderId}`,
                category: config.category,
                title,
                subtitle: order.customer?.name,
                badge: config.badge,
                badgeColor: 'secondary' as const,
                score: scoreCommandSearchFields(ctx.query, {
                    title,
                    subtitle: order.customer?.name,
                    identifiers,
                    keywords: [order.customerPO, order.orderNo, order.invoiceNo],
                    type: config.badge,
                    category: config.category,
                }),
                providerId: config.id,
                recentMeta: {
                    actionKey: 'scoped-order',
                    actionPayload: {
                        kind: config.category,
                        orderId,
                        customerNo: order.customer?.customerNo,
                        customerErpId: order.customer?.id,
                        customerName: order.customer?.name,
                    },
                },
                onSelect: () => navigateToScopedOrder(ctx, order, config.buildRoute),
            };
        });
    },
    enabled: (ctx) => {
        const services = getServices(ctx);
        return Boolean(services?.canAccess?.(config.accessKey)) && ctx.query.trim().length > 0;
    },
});

const SCOPED_ORDER_ROUTE_BUILDERS: Record<string, (customerId: number, orderId: number) => string> = {
    quotes: routeFunctions.customer.sales.quotes.single,
    salesOrders: routeFunctions.customer.sales.salesOrder.single,
    backOrders: routeFunctions.customer.sales.backOrder.single,
    invoices: routeFunctions.customer.sales.invoices.single,
    credits: routeFunctions.customer.sales.credits.single,
};

/**
 * Replays a stored scoped-order recent entry (re-resolves online customer id when needed).
 * Returns false to keep the palette open if navigation cannot complete.
 */
export const replayScopedOrderRecent = (
    payload: Record<string, string | number | boolean | undefined>,
    ctx: CommandSearchContext,
): Promise<boolean | void> | boolean => {
    const kind = typeof payload.kind === 'string' ? payload.kind : undefined;
    const orderId = typeof payload.orderId === 'number'
        ? payload.orderId
        : (typeof payload.orderId === 'string' ? Number(payload.orderId) : NaN);
    const buildRoute = kind ? SCOPED_ORDER_ROUTE_BUILDERS[kind] : undefined;

    if (!buildRoute || !Number.isFinite(orderId) || orderId <= 0) {
        return false;
    }

    const order: ScopedOrderResult = {
        id: orderId,
        orderId,
        customer: {
            id: payload.customerErpId as number | string | undefined,
            customerNo: typeof payload.customerNo === 'string' ? payload.customerNo : undefined,
            name: typeof payload.customerName === 'string' ? payload.customerName : undefined,
        },
    };

    return navigateToScopedOrder(ctx, order, buildRoute);
};

const quotesSearchProvider = createOrderSearchProvider({
    id: 'quotes-search',
    category: 'quotes',
    accessKey: 'sales.quotesList',
    badge: 'Quote',
    uppercaseCustomerFilterKey: true,
    fetch: Api.searchQuotes as unknown as CreateOrderSearchProviderConfig['fetch'],
    buildRoute: routeFunctions.customer.sales.quotes.single,
});

const salesOrdersSearchProvider = createOrderSearchProvider({
    id: 'sales-orders-search',
    category: 'salesOrders',
    accessKey: 'sales.salesOrdersList',
    badge: 'Sales Order',
    fetch: Api.searchSalesOrders as unknown as CreateOrderSearchProviderConfig['fetch'],
    buildRoute: routeFunctions.customer.sales.salesOrder.single,
});

const backOrdersSearchProvider = createOrderSearchProvider({
    id: 'back-orders-search',
    category: 'backOrders',
    accessKey: 'sales.salesOrdersList',
    badge: 'Back Order',
    fetch: Api.searchBackOrders as unknown as CreateOrderSearchProviderConfig['fetch'],
    buildRoute: routeFunctions.customer.sales.backOrder.single,
});

const invoicesSearchProvider = createOrderSearchProvider({
    id: 'invoices-search',
    category: 'invoices',
    accessKey: 'sales.invoicesList',
    badge: 'Invoice',
    getTitle: (order) => order.invoiceNo ?? order.orderNo,
    fetch: Api.searchInvoices as unknown as CreateOrderSearchProviderConfig['fetch'],
    buildRoute: routeFunctions.customer.sales.invoices.single,
});

const creditsSearchProvider = createOrderSearchProvider({
    id: 'credits-search',
    category: 'credits',
    accessKey: 'sales.creditsList',
    badge: 'Credit',
    getTitle: (order) => order.invoiceNo ?? order.orderNo,
    fetch: Api.searchCredits as unknown as CreateOrderSearchProviderConfig['fetch'],
    buildRoute: routeFunctions.customer.sales.credits.single,
});

const WARRANTY_RESULT_LIMIT = 10;
const RGA_SUBTITLE_FIELD_LIMIT = 5;

interface PartSupportReportRow {
    id?: number | string;
    rga?: string;
    customerName?: string;
    type?: string;
    invoiceNo?: string;
    serialNo?: string;
    partNo?: string;
    status?: string;
    reasonTitle?: string;
    vendorCode?: string;
    vendorStatus?: string;
}

interface RgaSubtitleField {
    label: string;
    value: string;
}

/**
 * RGA subtitle candidates in importance order. Labels resolve through
 * `tCommandSearch` (`rgaFields.*`) with English fallbacks.
 */
const RGA_SUBTITLE_CANDIDATES: Array<{
    selectLabel: (t: any) => string;
    fallbackLabel: string;
    getValue: (row: PartSupportReportRow) => string | undefined;
}> = [
    {selectLabel: (t) => t.rgaFields.customer, fallbackLabel: 'Customer', getValue: (row) => row.customerName},
    {selectLabel: (t) => t.rgaFields.type, fallbackLabel: 'Type', getValue: (row) => row.type},
    {selectLabel: (t) => t.rgaFields.invoice, fallbackLabel: 'Invoice', getValue: (row) => row.invoiceNo},
    {selectLabel: (t) => t.rgaFields.serial, fallbackLabel: 'Serial', getValue: (row) => row.serialNo},
    {selectLabel: (t) => t.rgaFields.part, fallbackLabel: 'Part', getValue: (row) => row.partNo},
    {selectLabel: (t) => t.rgaFields.status, fallbackLabel: 'Status', getValue: (row) => row.status},
    {selectLabel: (t) => t.rgaFields.reason, fallbackLabel: 'Reason', getValue: (row) => row.reasonTitle},
    {selectLabel: (t) => t.rgaFields.vendorCode, fallbackLabel: 'Vendor Code', getValue: (row) => row.vendorCode},
    {selectLabel: (t) => t.rgaFields.vendorStatus, fallbackLabel: 'Vendor Status', getValue: (row) => row.vendorStatus},
];

/**
 * Collects only the RGA row fields whose values match the query (the fields the
 * reporting keyword search cross-referenced), capped for palette density.
 */
const collectMatchedRgaFields = (
    row: PartSupportReportRow,
    query: string,
    services?: DynamicSearchServices,
): RgaSubtitleField[] => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return [];
    }

    return RGA_SUBTITLE_CANDIDATES
        .map((candidate) => ({candidate, value: candidate.getValue(row)?.trim()}))
        .filter((entry): entry is { candidate: typeof RGA_SUBTITLE_CANDIDATES[number]; value: string } =>
            Boolean(entry.value) && entry.value!.toLowerCase().includes(normalizedQuery))
        .slice(0, RGA_SUBTITLE_FIELD_LIMIT)
        .map(({candidate, value}) => ({
            label: services?.tCommandSearch?.(candidate.selectLabel) ?? candidate.fallbackLabel,
            value,
        }));
};

/** Warranty & return (RGA) reporting keyword search with query-matched subtitles. */
const warrantyReturnSearchProvider: CommandSearchProvider = {
    id: 'warranty-return-search',
    category: 'warranty',
    search: async (ctx) => {
        const services = getServices(ctx);
        const keyword = ctx.query.trim();
        const cancelSource = abortToCancelToken(ctx.abortController);

        const response = await Api.reportPartSupportReq({
            reportingBaseModel: {keyword, filters: []},
            pagination: {currentPage: 1, pageSize: WARRANTY_RESULT_LIMIT},
            sortBy: [],
        }, cancelSource.token);
        if (!response?.resultFlag) {
            return [];
        }

        const rows = (response.data?.items ?? []) as PartSupportReportRow[];
        const badge = services?.tCommandSearch?.((t) => t.categories.warranty) ?? 'Warranty';

        return rows
            .slice(0, WARRANTY_RESULT_LIMIT)
            .filter((row): row is PartSupportReportRow & { id: number | string } => row.id != null)
            .map((row) => {
                const title = row.rga ?? String(row.id);
                const matchedFields = collectMatchedRgaFields(row, keyword, services);
                const subtitle = matchedFields.length
                    ? matchedFields.map((field) => `${field.label}: ${field.value}`).join(' · ')
                    : undefined;
                const matchedValues = matchedFields.map((field) => field.value);
                const to = routeFunctions.warrantyAndReturn.single(Number(row.id));

                return {
                    id: `warranty-return-${row.id}`,
                    category: 'warranty',
                    title,
                    subtitle,
                    badge,
                    badgeColor: 'warning' as const,
                    score: scoreCommandSearchFields(keyword, {
                        title,
                        subtitle,
                        identifiers: [row.rga ?? String(row.id), ...matchedValues],
                        keywords: matchedValues,
                        type: 'Warranty',
                        category: 'warranty',
                    }),
                    providerId: 'warranty-return-search',
                    to,
                    recentMeta: {
                        actionKey: 'navigate',
                        actionPayload: {to},
                    },
                    onSelect: () => ctx.navigate(to),
                };
            });
    },
    enabled: (ctx) => {
        const services = getServices(ctx);
        return Boolean(services?.canAccess?.('warrantyAndReturn.viewList')) && ctx.query.trim().length > 0;
    },
};

/** Dynamic (API-backed) command-search providers for the operator panel. */
export const operatorDynamicSearchProviders: CommandSearchProvider[] = [
    vinDecoderProvider,
    nexusSearchProvider,
    customersSearchProvider,
    quotesSearchProvider,
    salesOrdersSearchProvider,
    backOrdersSearchProvider,
    invoicesSearchProvider,
    creditsSearchProvider,
    warrantyReturnSearchProvider,
];
